/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import { css, jsx } from '@emotion/react';
import { Fragment } from 'react';

import { InlineContext } from '../../../../context/inline';
import { type ComponentPropsConfig } from '../../../utils/codegen';

export const componentPropsConfig: ComponentPropsConfig = {
	align: true,
	as: true,
	color: true,
	maxLines: true,
	size: true,
	weight: true,
	children: true,
	testId: true,
};

/**
 * THIS SECTION WAS CREATED VIA CODEGEN DO NOT MODIFY {@see http://go/af-codegen}
 *
 * This component was generated against an existing platform component.
 * If you are seeing lint errors in this file, it means that this file's dependencies have been modified and the component will need to be regenerated.
 *
 * Please view https://hello.atlassian.net/wiki/spaces/ECO/pages/3358130330/How+to+Regenerate+UI+Kit+Components for more information on how to proceed.
 *
 * For further questions, please ping the #help-forge-ui channel.
 *
 * @codegen <<SignedSource::52a162686193a375589949e3b2175cd2>>
 * @codegenId #text
 * @codegenCommand yarn workspace @atlassian/forge-ui codegen text
 * @codegenDependency ../../../../../../../design-system/primitives/src/components/text.tsx <<SignedSource::5813dec2c60185512a09dbc10de528ff>>
 * @codegenDependency ../../../../../../../design-system/primitives/src/components/types.tsx <<SignedSource::9529629bd4dfcf934c9dfc41516de683>>
 * @codegenDependency ../../../../../../../design-system/primitives/src/utils/has-text-ancestor-context.tsx <<SignedSource::2c32107758c17743345dbf792b42fccc>>
 * @codegenDependency ../../../../../../../design-system/primitives/src/utils/surface-provider.tsx <<SignedSource::b2f47f7dfba2a18d1e5f39f5b93f4959>>
 * @codegenDependency ../../../../../../../design-system/primitives/src/xcss/style-maps.partial.tsx <<SignedSource::764fa2a11181a03d6d87ec2b92e13239>>
 */
/* eslint @repo/internal/codegen/signed-source-integrity: "warn" */
import React from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import { Text as PlatformText } from '@atlaskit/primitives';

type PlatformTextProps = React.ComponentProps<typeof PlatformText>;

export type TextProps = Pick<
  PlatformTextProps,
  'align' | 'as' | 'color' | 'maxLines' | 'size' | 'weight' | 'children' | 'testId'
>;

/**
 * Primitives are token-backed low-level building blocks.
 */
export const Text = (props: Parameters<RenderFn>[0]) => {
  const {
    align,
    as,
    color,
    maxLines,
    size,
    weight,
    testId,
  } = props.forgeDoc.props as TextProps;
  return (
    <PlatformText
      align={align}
      as={as}
      color={color}
      maxLines={maxLines}
      size={size}
      weight={weight}
      children={props.forgeDoc.children.map(c => <React.Fragment key={c.key}>{props.render(c)}</React.Fragment>)}
      testId={testId}
    />
  );
};
/**
 * @codegenEnd
 */


// Workaround #1: this can be removed once we no longer need the wrapper <p> tag
// Reset the value for "overflowWrap" to ensure backwards compatibility with the old component
/* eslint-disable */
const fixTextWrapStyles = css({
	'& > *': {
		overflowWrap: 'inherit !important' as any,
	},
});
/* eslint-enable */

// Workaround #2: this can be removed once "text-decoration" is supported by the Platform "Text" component
const strikeThroughStyles = css({
	textDecoration: 'line-through',
});

export type PatchedTextProps = Omit<TextProps, 'as'> & { as?: TextProps['as'] | 'strike' };

// Workaround #3: this can be removed once we no longer need the wrapper <p> tag
// The CSS property "text-align" doesn't work on non-block elements.
// So if we're rendering a <p> tag, we need to apply the alignment to the parent <p> tag
const textAlignMap = {
	center: css({ textAlign: 'center' }),
	end: css({ textAlign: 'end' }),
	start: css({ textAlign: 'start' }),
};

export const PatchedText = (props: Parameters<RenderFn>[0]) => {
	const textProps = props.forgeDoc.props as PatchedTextProps;

	// Remove the else case when the feature gate is removed
	// https://hello.jira.atlassian.cloud/browse/FD-88404
	// eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
	if (fg('forge-ui-use-ads-text-component')) {
		const componentProps = { ...props.forgeDoc.props } as PatchedTextProps;
		const align = componentProps.align;

		// Workaround #4: this can be removed once we no longer need the wrapper <p> tag
		// ADS Text will set value for "color" if none is provided. Since the only way to adjust the color of texts in
		// Forge UI "inherit" was by using the `xcss` prop on a <Box>, we need to make sure that inheritance of that color
		// is still possible, otherwise we would not be backwards compatible.
		// We can achieve this by setting the default value for "color" to "inherit" if it is not provided.
		componentProps.color = textProps.color === undefined ? 'inherit' : textProps.color;

		// ADS does currently not support "strike" as a value for "as", therefore we have to wrap the component
		// in a span with the strike-through style. Then, we can use the regular Text component with all the other props.
		if (textProps.as === 'strike') {
			componentProps.as = 'span';
			return (
				<span css={[fixTextWrapStyles, strikeThroughStyles]}>
					<Text {...props} forgeDoc={{ children: props.forgeDoc.children, type: props.forgeDoc.type, props: componentProps }} />
				</span>
			);
		}

		// In the non-ADS Text component, UI Kit defaulted to using a "p" tag to render. We need to replicate this behavior
		// for backwards compatibility with the old component. Then, we can use the Text component as a "span" with all the other props.
		if (textProps.as === 'p' || textProps.as === undefined) {
			componentProps.as = 'span';
			return (
				// eslint-disable-next-line
				<p css={[fixTextWrapStyles, align && textAlignMap[align]]}>
					<Text {...props} forgeDoc={{ children: props.forgeDoc.children, type: props.forgeDoc.type, props: componentProps }} />
				</p>
			);
		} else {
			return (
				<span css={fixTextWrapStyles}>
					<Text {...props} forgeDoc={{ children: props.forgeDoc.children, type: props.forgeDoc.type, props: componentProps }} />
				</span>
			);
		}
	} else {
		const { forgeDoc, render } = props;
		return (
			<InlineContext.Provider value={{ inline: true }}>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<p>
					{forgeDoc.children.map((child) => {
						// React expects a key on each child when rendering an array
						return <Fragment key={child.key}>{render(child)}</Fragment>;
					})}
				</p>
			</InlineContext.Provider>
		);
	}
};
